import { useEffect, useState } from "react";
import { toast } from "sonner";
import api, { formatErr } from "../lib/api";
import { useI18n } from "../lib/i18n";
import { BRAND_COLORS, withAlpha } from "../lib/bookingStatus";
import { deDate } from "../lib/dateFormat";

function Stars({ value, onChange, testid }) {
  return (
    <div className="flex gap-1" data-testid={testid}>
      {[1, 2, 3, 4, 5].map((n) => (
        <button key={n} type="button" onClick={() => onChange?.(n)}
          data-testid={`${testid}-star-${n}`}
          className="text-3xl leading-none focus:outline-none transition-colors"
          style={{ color: n <= (value || 0) ? BRAND_COLORS.gold : withAlpha(BRAND_COLORS.navy, 0.15) }}>
          ★
        </button>
      ))}
    </div>
  );
}

export default function CustomerRatings() {
  const { lang } = useI18n();
  const de = lang === "de";
  const [rows, setRows] = useState([]);
  const [drafts, setDrafts] = useState({});

  const load = async () => {
    try {
      const { data } = await api.get("/customer/completed-assignments");
      setRows(data);
    } catch (e) { toast.error(formatErr(e.response?.data?.detail) || e.message); }
  };
  useEffect(() => { load(); }, []);

  const submit = async (id) => {
    const d = drafts[id] || {};
    if (!d.stars) return toast.error(de ? "Bitte Sterne wählen." : "Please choose a star rating.");
    try {
      await api.post(`/assignments/${id}/rating`, { stars: d.stars, comment: d.comment || "" });
      toast.success(de ? "Danke für Ihre Bewertung!" : "Thank you for your rating!");
      await load();
    } catch (e) { toast.error(formatErr(e.response?.data?.detail) || e.message); }
  };

  const pending = rows.filter((r) => !r.rating);
  const rated = rows.filter((r) => r.rating);

  return (
    <div className="max-w-[900px] mx-auto px-6 md:px-10 py-10 md:py-14" data-testid="customer-ratings">
      <div className="mb-10">
        <div className="label-caps mb-3">{de ? "Ihre Meinung zählt" : "Your feedback matters"}</div>
        <h1 className="font-display text-4xl md:text-5xl text-navy">
          {de ? "Aufträge bewerten" : "Rate assignments"}
        </h1>
        <p className="text-[15px] text-navy-dark/70 mt-3 max-w-2xl">
          {de
            ? "Nach Abschluss jedes Auftrags freuen wir uns über eine kurze Rückmeldung — so verbessern wir uns kontinuierlich für Sie."
            : "After each completed assignment we welcome a short reply — this is how we keep improving for you."}
        </p>
      </div>

      {pending.length === 0 && rated.length === 0 ? (
        <div className="bg-white border border-navy/10 p-16 text-center">
          <div className="font-display text-2xl text-navy mb-2">
            {de ? "Keine abgeschlossenen Aufträge" : "No completed assignments"}
          </div>
        </div>
      ) : (        <>
          {pending.length > 0 && (
            <section className="mb-10">
              <h2 className="font-display text-2xl text-navy mb-4">{de ? "Zu bewerten" : "Awaiting your rating"}</h2>
              <div className="space-y-4">
                {pending.map((r) => (
                  <div key={r.id} data-testid={`rate-row-${r.id}`} className="bg-white border border-navy/10 p-6">
                    <div className="label-caps mb-2">
                      {de ? "Abgeschlossen am" : "Completed on"} {r.completed_at ? deDate(r.completed_at) : "—"}
                    </div>
                    <h3 className="font-display text-xl text-navy mb-4">{r.title}</h3>
                    <Stars testid={`stars-${r.id}`} value={drafts[r.id]?.stars || 0}
                      onChange={(v) => setDrafts({ ...drafts, [r.id]: { ...drafts[r.id], stars: v } })} />
                    <textarea rows={2} placeholder={de ? "Anmerkung (optional)" : "Comment (optional)"}
                      value={drafts[r.id]?.comment || ""}
                      data-testid={`rate-comment-${r.id}`}
                      onChange={(e) => setDrafts({ ...drafts, [r.id]: { ...drafts[r.id], comment: e.target.value } })}
                      className="w-full mt-3 bg-white border border-navy/20 focus:border-gold focus:outline-none px-3 py-2 text-[13px]" />
                    <button data-testid={`rate-submit-${r.id}`} onClick={() => submit(r.id)}
                      className="mt-3 text-ivory px-5 py-2 text-[11px] tracking-widest uppercase hover:opacity-90"
                      style={{ background: BRAND_COLORS.navy }}>
                      {de ? "Bewertung senden" : "Send rating"}
                    </button>
                  </div>
                ))}
              </div>
            </section>
          )}
          {rated.length > 0 && (
            <section>
              <h2 className="font-display text-2xl text-navy mb-4">
                {de ? "Ihre bisherigen Bewertungen" : "Your past ratings"}
              </h2>
              <div className="space-y-3">
                {rated.map((r) => (
                  <div key={r.id} className="bg-white border border-navy/10 p-5 flex items-center justify-between gap-4">
                    <div>
                      <div className="font-display text-lg text-navy">{r.title}</div>
                      {r.rating_comment && <div className="text-[13px] text-navy-dark/70 italic mt-1">„{r.rating_comment}"</div>}
                    </div>
                    <div className="text-2xl" style={{ color: BRAND_COLORS.gold }}>{"★".repeat(r.rating)}<span style={{ color: withAlpha(BRAND_COLORS.navy, 0.15) }}>{"★".repeat(5 - r.rating)}</span></div>
                  </div>
                ))}
              </div>
            </section>
          )}
        </>
      )}
    </div>
  );
}
